/**
 * Editor guidato di una voce di capitolato.
 *
 * Chi compila una voce non deve sapere come funziona il motore di confronto: deve capire, mentre
 * sceglie, **cosa leggera' il fornitore, come rispondera', come la risposta diventera' punteggio e
 * quanto peserà**. Per questo il form e' diviso in cinque passi in linguaggio semplice, ognuno con il
 * suo perche', e ha tre aiuti interattivi:
 *
 *   - un **simulatore**: tre offerte d'esempio e i punteggi che prenderebbero, calcolati con le
 *     stesse regole di backend/domain/scoring.js (replicate in piccolo qui sotto: se cambiano li',
 *     vanno cambiate anche qui — PHC_fornitori/schema/05-analisi.md);
 *   - un **riepilogo in parole** di come si comportera' la voce in gara;
 *   - un **controllo della descrizione**, che e' il testo che esce nel PDF al fornitore: avvisa se
 *     contiene condizioni attuali, nomi di fornitori in carica o importi.
 *
 * Tipo di risposta e formula sono legati: si propongono solo le formule che hanno senso per il tipo
 * scelto, e cambiando tipo si passa da sola alla formula consigliata se quella di prima non vale piu'.
 */

import React, { useMemo, useState } from "react";
import { Campo, Testo, AreaTesto, Numero, Scelta, Interruttore } from "./campi";
import {
  SEZIONE_VOCE,
  PUNTEGGIO_MASSIMO_DEFAULT,
  opzioni,
} from "../constants/optionSet";

const TR = {
  NUMERICO: 100000000,
  IMPORTO: 100000001,
  PERCENTUALE: 100000002,
  SI_NO: 100000003,
  TESTO: 100000004,
  DATA: 100000005,
};
const F = {
  PREZZO: 100000000,
  MIN_MAX: 100000001,
  MAX_MIN: 100000002,
  BOOLEANO: 100000003,
  MANUALE: 100000004,
};

const TIPI = [
  {
    v: TR.IMPORTO,
    titolo: "Un importo in €",
    esempio: "canone annuo, tariffa oraria, prezzo a pezzo",
  },
  {
    v: TR.NUMERICO,
    titolo: "Un numero",
    esempio: "ore di intervento, visite all'anno, posti letto",
  },
  {
    v: TR.PERCENTUALE,
    titolo: "Una percentuale",
    esempio: "sconto sul listino, royalty",
  },
  {
    v: TR.SI_NO,
    titolo: "Sì o No",
    esempio: "reperibilità 24h, certificazione posseduta",
  },
  {
    v: TR.TESTO,
    titolo: "Un testo libero",
    esempio: "metodologia, esclusioni, referenze",
  },
  { v: TR.DATA, titolo: "Una data", esempio: "data di avvio del servizio" },
];

const FORMULE = {
  [F.PREZZO]: {
    titolo: "Il più economico vince",
    spiega:
      "Chi offre il valore più basso prende il punteggio pieno; gli altri in proporzione (chi chiede il doppio prende la metà).",
  },
  [F.MAX_MIN]: {
    titolo: "Più basso è meglio",
    spiega:
      "Punteggio pieno al valore più basso, zero al più alto, in mezzo in proporzione. Adatto a tempi di intervento e durate.",
  },
  [F.MIN_MAX]: {
    titolo: "Più alto è meglio",
    spiega:
      "Punteggio pieno al valore più alto, zero al più basso, in mezzo in proporzione. Adatto a sconti, royalty, visite incluse.",
  },
  [F.BOOLEANO]: {
    titolo: "Sì prende tutto, No niente",
    spiega: "Risposta Sì = punteggio pieno, No = zero.",
  },
  [F.MANUALE]: {
    titolo: "Lo valuti tu",
    spiega:
      "In fase di confronto assegni un punteggio a mano, con una motivazione. Necessario per le risposte di testo.",
  },
};

/** Formule sensate per ogni tipo di risposta; la prima e' quella consigliata. */
const FORMULE_PER_TIPO = {
  [TR.IMPORTO]: [F.PREZZO, F.MAX_MIN, F.MIN_MAX, F.MANUALE],
  [TR.NUMERICO]: [F.MAX_MIN, F.MIN_MAX, F.PREZZO, F.MANUALE],
  [TR.PERCENTUALE]: [F.MIN_MAX, F.MAX_MIN, F.MANUALE],
  [TR.SI_NO]: [F.BOOLEANO, F.MANUALE],
  [TR.TESTO]: [F.MANUALE],
  [TR.DATA]: [F.MANUALE],
};

const SPIEGA_SEZIONE = {
  100000000: "requisiti tecnici e di servizio",
  100000001: "prezzi, canoni, tariffe, sconti",
  100000002: "livelli di servizio: tempi di intervento, reperibilità",
  100000003: "certificazioni, personale, rendicontazione",
  100000004: "durata, pagamenti, penali, recesso",
};

const UNITA_SUGGERITE = {
  [TR.IMPORTO]: ["€/anno", "€/mese", "€/ora", "€/pezzo"],
  [TR.NUMERICO]: ["ore", "giorni", "visite/anno", "mesi"],
  [TR.PERCENTUALE]: ["%"],
};

const numerico = (t) => [TR.NUMERICO, TR.IMPORTO, TR.PERCENTUALE].includes(t);
const n = (x) => (x === "" || x == null ? null : Number(x));
const fmt = (x) =>
  x == null
    ? "—"
    : Number(x).toLocaleString("it-IT", { maximumFractionDigits: 2 });

/**
 * Le stesse regole di `conformita` e `punteggioVoce` in backend/domain/scoring.js, sui soli casi
 * numerici e su una voce sola: un'offerta esclusa (voce obbligatoria senza risposta o fuori soglia)
 * non prende punti e non conta per i minimi e massimi delle altre; fuori soglia su una voce non
 * obbligatoria e' «non conforme» ma resta in gara.
 * @returns {{ punti: number|null, stato: "esclusa"|"non conforme"|null }[]}
 */
function simula(
  formula,
  max,
  valori,
  { sogliaMinima, sogliaMassima, obbligatorio },
) {
  const smin = n(sogliaMinima);
  const smax = n(sogliaMassima);
  const righe = valori.map((raw) => {
    const x = n(raw);
    const fuori =
      x != null && ((smin != null && x < smin) || (smax != null && x > smax));
    const nonConforme = x == null ? obbligatorio : fuori;
    return {
      x,
      stato: nonConforme ? (obbligatorio ? "esclusa" : "non conforme") : null,
    };
  });
  const v = righe
    .filter((r) => r.stato !== "esclusa" && r.x != null && Number.isFinite(r.x))
    .map((r) => r.x);
  const min = v.length ? Math.min(...v) : null;
  const mx = v.length ? Math.max(...v) : null;
  const positivi = v.filter((x) => x > 0);
  const minPos = positivi.length ? Math.min(...positivi) : null;
  return righe.map(({ x, stato }) => {
    if (stato === "esclusa") return { punti: null, stato };
    if (x == null || min == null) return { punti: x == null ? 0 : null, stato };
    let punti = null;
    if (formula === F.PREZZO)
      punti = x > 0 && minPos ? Math.min(max, (max * minPos) / x) : 0;
    if (formula === F.MIN_MAX)
      punti = mx === min ? max : Math.max(0, (max * (x - min)) / (mx - min));
    if (formula === F.MAX_MIN)
      punti = mx === min ? max : Math.max(0, (max * (mx - x)) / (mx - min));
    return { punti, stato };
  });
}

/** Segnali di testo che non dovrebbe uscire nel PDF al fornitore. */
function avvisiDescrizione(testo) {
  const t = testo || "";
  const avvisi = [];
  if (
    /\boggi\b|attualmente|\bcontratt[oi] (attual|in essere|vigent)|fornitore (attuale|uscente)/i.test(
      t,
    )
  ) {
    avvisi.push("cita le condizioni di oggi («oggi…», «contratto attuale»)");
  }
  if (/\d[\d.,]*\s?(€|euro|EUR)\b|€\s?\d/i.test(t)) {
    avvisi.push("contiene importi: rischi di suggerire il prezzo da battere");
  }
  return avvisi;
}

function Passo({ numero, titolo, perche, children }) {
  return (
    <section className="rounded-md border border-gray-200 bg-white p-4 space-y-3">
      <div>
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-acquisti-600 text-xs text-white">
            {numero}
          </span>
          {titolo}
        </h4>
        {perche && <p className="text-sm text-gray-600 mt-1 ml-8">{perche}</p>}
      </div>
      <div className="ml-0 md:ml-8 space-y-3">{children}</div>
    </section>
  );
}

function Scheda({ attiva, onClick, titolo, sotto, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-left rounded-md border px-3 py-2 transition ${
        attiva
          ? "border-acquisti-600 bg-acquisti-50 ring-1 ring-acquisti-600"
          : "border-gray-200 hover:border-acquisti-300 bg-white"
      } disabled:opacity-40`}
    >
      <span className="block text-sm font-medium text-gray-900">{titolo}</span>
      {sotto && (
        <span className="block text-xs text-gray-500 mt-0.5">{sotto}</span>
      )}
    </button>
  );
}

const VUOTA = {
  nome: "",
  sezione: "",
  ordine: "",
  descrizione: "",
  obbligatorio: false,
  peso: "",
  punteggioMassimo: PUNTEGGIO_MASSIMO_DEFAULT,
  tipoRisposta: "",
  unitaMisura: "",
  formula: "",
  sogliaMinima: "",
  sogliaMassima: "",
  note: "",
  criterioValutazioneId: "",
  servizioId: "",
};

export default function FormVoceGuidato({
  iniziale,
  onSalva,
  onAnnulla,
  criteri,
  servizi,
  inCorso,
  pesoAltreVoci = 0,
  master = false,
}) {
  // I null del server diventano "": e' la convenzione dei campi (vedi components/campi.jsx).
  const [v, setV] = useState(() => ({
    ...VUOTA,
    ...Object.fromEntries(
      Object.entries(iniziale || {}).map(([k, x]) => [k, x ?? ""]),
    ),
  }));
  const [esempi, setEsempi] = useState(["", "", ""]);
  const set = (k) => (val) => setV((x) => ({ ...x, [k]: val }));

  const tipo = n(v.tipoRisposta);
  const formula = n(v.formula);
  const max = n(v.punteggioMassimo) || PUNTEGGIO_MASSIMO_DEFAULT;
  const peso = n(v.peso) || 0;
  const formuleAmmesse = tipo != null ? FORMULE_PER_TIPO[tipo] || [] : [];
  const formulaIncoerente =
    formula != null && tipo != null && !formuleAmmesse.includes(formula);

  const scegliTipo = (t) => {
    setV((x) => {
      const ammesse = FORMULE_PER_TIPO[t] || [];
      const f = n(x.formula);
      return {
        ...x,
        tipoRisposta: t,
        formula: f != null && ammesse.includes(f) ? f : (ammesse[0] ?? ""),
        // Soglie e unita' hanno senso solo per i numeri.
        ...(numerico(t)
          ? {}
          : { sogliaMinima: "", sogliaMassima: "", unitaMisura: "" }),
      };
    });
    setEsempi(
      t === TR.IMPORTO
        ? ["1000", "1200", "1500"]
        : t === TR.PERCENTUALE
          ? ["5", "10", "15"]
          : ["4", "8", "24"],
    );
  };

  const totale = pesoAltreVoci + peso;
  const quota = totale > 0 ? (100 * peso) / totale : 0;
  const punteggi = useMemo(
    () =>
      numerico(tipo)
        ? simula(formula, max, esempi, {
            sogliaMinima: v.sogliaMinima,
            sogliaMassima: v.sogliaMassima,
            obbligatorio: !!v.obbligatorio,
          })
        : [],
    [
      tipo,
      formula,
      max,
      esempi,
      v.sogliaMinima,
      v.sogliaMassima,
      v.obbligatorio,
    ],
  );
  const avvisi = avvisiDescrizione(v.descrizione);
  const unita = v.unitaMisura ? ` ${v.unitaMisura}` : "";

  // Riepilogo in parole: e' il controllo finale per chi compila.
  const frasi = [];
  if (tipo != null) {
    frasi.push(
      `Il fornitore risponde con ${
        {
          [TR.IMPORTO]: "un importo",
          [TR.NUMERICO]: "un numero",
          [TR.PERCENTUALE]: "una percentuale",
          [TR.SI_NO]: "Sì o No",
          [TR.TESTO]: "un testo",
          [TR.DATA]: "una data",
        }[tipo]
      }${unita ? ` (${v.unitaMisura})` : ""}.`,
    );
  }
  if (formula != null && FORMULE[formula]) {
    frasi.push(
      `Punteggio: ${FORMULE[formula].titolo.toLowerCase()}, fino a ${fmt(max)} punti.`,
    );
  }
  frasi.push(
    peso > 0
      ? `Vale circa il ${fmt(quota)}% del punteggio ${master ? "del Pacchetto" : "del capitolato"}.`
      : "Con peso 0 non conta nel punteggio: serve solo come requisito o come informazione.",
  );
  if (v.obbligatorio) {
    frasi.push(
      tipo === TR.SI_NO
        ? "È obbligatoria: chi risponde No, o non risponde, viene escluso."
        : numerico(tipo) && (v.sogliaMinima !== "" || v.sogliaMassima !== "")
          ? "È obbligatoria: chi non risponde o esce dalle soglie viene escluso."
          : numerico(tipo)
            ? "È obbligatoria: chi non risponde viene escluso."
            : "È obbligatoria: la conformità la giudichi tu in confronto; senza risposta l'offerta è esclusa.",
    );
  } else if (
    numerico(tipo) &&
    (v.sogliaMinima !== "" || v.sogliaMassima !== "")
  ) {
    frasi.push(
      "Fuori soglia la risposta è segnalata come non conforme, ma l'offerta resta in gara.",
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSalva(v);
      }}
      className="rounded-md border border-acquisti-200 bg-acquisti-50/40 p-4 space-y-4"
    >
      {/* 1 — cosa si chiede */}
      <Passo
        numero={1}
        titolo="Cosa chiedi al fornitore"
        perche="Il nome e la descrizione escono nella richiesta d'offerta: è quello che il fornitore legge e a cui risponde."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Campo
            label="Voce *"
            className="md:col-span-2"
            aiuto="Un titolo breve, come apparirà nel PDF e nella tabella di confronto."
          >
            <Testo
              valore={v.nome}
              onChange={set("nome")}
              required
              placeholder="es. Tempo di intervento su guasto"
            />
          </Campo>
          <Campo
            label="Sezione"
            aiuto={
              SPIEGA_SEZIONE[n(v.sezione)] ||
              "Raggruppa le voci nel PDF e nel riepilogo dei pesi."
            }
          >
            <Scelta
              valore={v.sezione}
              onChange={set("sezione")}
              opzioni={opzioni(SEZIONE_VOCE)}
            />
          </Campo>
          {servizi.length > 1 && (
            <Campo
              label="Servizio"
              className="md:col-span-3"
              aiuto="A quale dei servizi del capitolato si riferisce la voce. Le voci economiche e contrattuali di solito valgono per tutti."
            >
              <Scelta
                valore={v.servizioId}
                onChange={set("servizioId")}
                opzioni={servizi.map((t) => ({ value: t.id, label: t.nome }))}
                vuoto="comune a tutti i servizi"
              />
            </Campo>
          )}
          <Campo
            label="Descrizione per il fornitore"
            className="md:col-span-3"
            aiuto="Scrivi il requisito: cosa deve garantire o dichiarare. Non le condizioni di oggi, i nomi dei fornitori attuali o i prezzi che paghiamo: quelli vanno nelle note interne."
          >
            <AreaTesto
              valore={v.descrizione}
              onChange={set("descrizione")}
              righe={3}
            />
          </Campo>
        </div>
        {avvisi.length > 0 && (
          <p className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
            ⚠️ La descrizione {avvisi.join(" e ")}. Il fornitore la leggerà:
            sposta questi dati nelle note interne.
          </p>
        )}
      </Passo>

      {/* 2 — come risponde */}
      <Passo
        numero={2}
        titolo="Come risponde il fornitore"
        perche="Decide cosa compilerà il fornitore e quali modi di dare il punteggio sono possibili."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {TIPI.map((t) => (
            <Scheda
              key={t.v}
              attiva={tipo === t.v}
              onClick={() => scegliTipo(t.v)}
              titolo={t.titolo}
              sotto={`es. ${t.esempio}`}
            />
          ))}
        </div>
        {numerico(tipo) && (
          <Campo
            label="Unità di misura"
            aiuto="Aiuta il fornitore a rispondere nella stessa unità di tutti gli altri: senza, i valori non sono confrontabili."
          >
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-48">
                <Testo
                  valore={v.unitaMisura}
                  onChange={set("unitaMisura")}
                  placeholder="es. €/anno"
                />
              </div>
              {(UNITA_SUGGERITE[tipo] || []).map((u) => (
                <button
                  key={u}
                  type="button"
                  className="rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:border-acquisti-500"
                  onClick={() => set("unitaMisura")(u)}
                >
                  {u}
                </button>
              ))}
            </div>
          </Campo>
        )}
      </Passo>

      {/* 3 — punteggio */}
      <Passo
        numero={3}
        titolo="Come diventa punteggio"
        perche="Quando arrivano le offerte, la risposta di ciascun fornitore viene trasformata in punti con questa regola."
      >
        {tipo == null ? (
          <p className="text-sm text-gray-500">
            Scegli prima come risponde il fornitore.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {formuleAmmesse.map((f, i) => (
              <Scheda
                key={f}
                attiva={formula === f}
                onClick={() => set("formula")(f)}
                titolo={`${FORMULE[f].titolo}${i === 0 ? " · consigliata" : ""}`}
                sotto={FORMULE[f].spiega}
              />
            ))}
          </div>
        )}
        {formulaIncoerente && (
          <p className="text-sm text-red-700">
            La formula scelta non funziona con questo tipo di risposta: scegline
            una tra quelle proposte.
          </p>
        )}

        {numerico(tipo) &&
          [F.PREZZO, F.MIN_MAX, F.MAX_MIN].includes(formula) && (
            <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-800">
                Prova con tre offerte d'esempio
              </p>
              <p className="text-xs text-gray-500 mb-2">
                Scrivi dei valori verosimili e guarda quanti punti prenderebbe
                ciascuno (su {fmt(max)}).
              </p>
              <div className="grid grid-cols-3 gap-3 max-w-lg">
                {esempi.map((e, i) => (
                  <div key={i} className="space-y-1">
                    <span className="text-xs text-gray-500">
                      Fornitore {"ABC"[i]}
                      {unita && ` (${v.unitaMisura})`}
                    </span>
                    <input
                      type="number"
                      className="input"
                      value={e}
                      onChange={(ev) =>
                        setEsempi((s) =>
                          s.map((x, j) => (j === i ? ev.target.value : x)),
                        )
                      }
                    />
                    <div className="h-2 rounded bg-gray-200 overflow-hidden">
                      <div
                        className="h-2 bg-acquisti-600"
                        style={{
                          width: `${punteggi[i]?.punti != null ? (100 * punteggi[i].punti) / max : 0}%`,
                        }}
                      />
                    </div>
                    {punteggi[i]?.stato === "esclusa" ? (
                      <span className="text-sm font-semibold text-red-700">
                        esclusa
                      </span>
                    ) : (
                      <span className="text-sm font-semibold text-gray-900">
                        {fmt(punteggi[i]?.punti)} punti
                        {punteggi[i]?.stato === "non conforme" && (
                          <span className="block text-xs font-normal text-yellow-700">
                            fuori soglia, resta in gara
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
      </Passo>

      {/* 4 — peso */}
      <Passo
        numero={4}
        titolo="Quanto conta nel confronto"
        perche="Il peso dice quanto questa voce incide sul punteggio finale rispetto alle altre. Non serve che i pesi sommino a 100: contano le proporzioni."
      >
        <div className="flex flex-wrap items-end gap-4">
          <Campo label="Peso">
            <div className="w-28">
              <Numero
                valore={v.peso}
                onChange={set("peso")}
                min="0"
                max="100"
                step="1"
              />
            </div>
          </Campo>
          <div className="flex-1 min-w-[16rem]">
            <div className="h-3 rounded bg-gray-200 overflow-hidden">
              <div
                className="h-3 bg-acquisti-600"
                style={{ width: `${Math.min(100, quota)}%` }}
              />
            </div>
            <p className="text-sm text-gray-700 mt-1">
              {peso > 0 ? (
                <>
                  Circa <strong>{fmt(quota)}%</strong> del punteggio{" "}
                  {master ? "del Pacchetto" : "del capitolato"} (peso{" "}
                  {fmt(peso)} su {fmt(totale)} in totale).
                </>
              ) : (
                "Peso 0: la voce non entra nel punteggio."
              )}
            </p>
          </div>
        </div>
      </Passo>

      {/* 5 — requisito */}
      <Passo
        numero={5}
        titolo="È un requisito indispensabile?"
        perche="Una voce obbligatoria non conforme esclude l'offerta dal confronto, qualunque sia il suo punteggio. Usala per ciò senza cui il servizio non va bene."
      >
        <Interruttore
          valore={v.obbligatorio}
          onChange={set("obbligatorio")}
          testo="Sì, se non è soddisfatto l'offerta viene esclusa"
        />
        {numerico(tipo) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
            <Campo
              label="Valore minimo accettabile"
              aiuto="Sotto questo valore la risposta non è conforme (es. sconto almeno 5%)."
            >
              <Numero
                valore={v.sogliaMinima}
                onChange={set("sogliaMinima")}
                step="0.01"
              />
            </Campo>
            <Campo
              label="Valore massimo accettabile"
              aiuto="Sopra questo valore la risposta non è conforme (es. intervento entro 24 ore)."
            >
              <Numero
                valore={v.sogliaMassima}
                onChange={set("sogliaMassima")}
                step="0.01"
              />
            </Campo>
          </div>
        )}
        {[TR.TESTO, TR.DATA].includes(tipo) && v.obbligatorio && (
          <p className="text-sm text-gray-600">
            Per testo e date il sistema non può giudicare da solo: in confronto
            indicherai tu se la risposta è conforme.
          </p>
        )}
      </Passo>

      {/* opzioni avanzate */}
      <details className="rounded-md border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-medium text-gray-800">
          Opzioni avanzate e note interne
        </summary>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <Campo
            label="Ordine nella sezione"
            aiuto="Posizione nel PDF; si cambia anche con le frecce nell'elenco."
          >
            <Numero valore={v.ordine} onChange={set("ordine")} />
          </Campo>
          <Campo
            label="Punteggio massimo"
            aiuto="È solo la scala su cui si danno i punti (default 10): quanto conta la voce lo decide il peso, non questo numero."
          >
            <Numero
              valore={v.punteggioMassimo}
              onChange={set("punteggioMassimo")}
              step="1"
              min="1"
              max="100"
            />
          </Campo>
          <Campo
            label="Criterio di performance"
            aiuto="Facoltativo. Per il fornitore uscente il punteggio di questa voce viene preso dalla sua ultima valutazione approvata su questo criterio, invece che dalla risposta: così si confronta anche come ha lavorato davvero."
          >
            <Scelta
              valore={v.criterioValutazioneId}
              onChange={set("criterioValutazioneId")}
              opzioni={criteri.map((c) => ({ value: c.id, label: c.nome }))}
              vuoto="nessuno"
            />
          </Campo>
          <Campo
            label="Note interne"
            className="md:col-span-3"
            aiuto="Non escono nel PDF: qui vanno le condizioni attuali, le fonti, i ragionamenti sui pesi."
          >
            <AreaTesto valore={v.note} onChange={set("note")} righe={2} />
          </Campo>
        </div>
      </details>

      {/* riepilogo + azioni */}
      <div className="rounded-md border border-acquisti-200 bg-white p-3">
        <p className="text-xs uppercase tracking-wide text-acquisti-700 font-semibold">
          In pratica
        </p>
        <p className="text-sm text-gray-800 mt-1">{frasi.join(" ")}</p>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onAnnulla}>
          Annulla
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={inCorso || formulaIncoerente}
        >
          {inCorso ? "Salvataggio…" : "Salva voce"}
        </button>
      </div>
    </form>
  );
}
